import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import StoryCard from '@/components/StoryCard';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, X, Sparkles, PenLine, Users, ArrowRight,
} from 'lucide-react';

const PREVIEW_LIMIT = 12;

function AuthorAvatar({ name, src }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-12 h-12 rounded-full object-cover border border-border"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-ruby/40 to-surface flex items-center justify-center border border-border">
      <span className="font-brand text-lg font-bold text-white">{initial}</span>
    </div>
  );
}

export default function Browse() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [intimateStories, setIntimateStories] = useState([]);
  const [communityStories, setCommunityStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [communityAuthors, setCommunityAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: category || '',
    search: '',
    sortBy: 'recent',
    authorId: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [catsRes, authorsRes] = await Promise.all([
          api.get('/categories').catch(() => ({ data: [] })),
          api.get('/authors/community?limit=100').catch(() => ({ data: [] })),
        ]);
        setCategories(catsRes.data || []);
        setCommunityAuthors(authorsRes.data || []);
      } catch (error) {
        console.error('Error fetching categories/authors:', error);
      }
    })();
  }, []);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, category: category || '' }));
  }, [category]);

  useEffect(() => {
    const fetchPreviews = async () => {
      setLoading(true);
      try {
        const baseParams = new URLSearchParams({ limit: String(PREVIEW_LIMIT) });
        if (filters.category) baseParams.append('category', filters.category);
        if (filters.search) baseParams.append('search', filters.search);

        const [intRes, comRes] = await Promise.all([
          api.get(`/stories?${baseParams.toString()}&owner_kind=intimate`).catch(() => ({ data: [] })),
          api.get(`/stories?${baseParams.toString()}&owner_kind=community`).catch(() => ({ data: [] })),
        ]);

        const sortFn = (arr) => {
          if (filters.sortBy === 'popular') return [...arr].sort((a, b) => b.views - a.views);
          if (filters.sortBy === 'rating') return [...arr].sort((a, b) => b.rating - a.rating);
          return arr;
        };
        setIntimateStories(sortFn(intRes.data || []));
        setCommunityStories(sortFn(comRes.data || []));
      } catch (error) {
        console.error('Error fetching preview stories:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchPreviews, 300);
    return () => clearTimeout(debounce);
  }, [filters.category, filters.search, filters.sortBy]);

  const currentCategory = categories.find((c) => c.slug === filters.category);

  const communityFiltered = filters.authorId
    ? communityStories.filter((s) => s.author_id === filters.authorId)
    : communityStories;

  const selectedAuthor = communityAuthors.find((a) => a.id === filters.authorId);
  // Preserve current filters when navigating to the dedicated listing pages.
  const querySuffix = (() => {
    const p = new URLSearchParams();
    if (filters.category) p.append('category', filters.category);
    if (filters.search) p.append('search', filters.search);
    if (filters.sortBy && filters.sortBy !== 'recent') p.append('sort', filters.sortBy);
    const s = p.toString();
    return s ? `?${s}` : '';
  })();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Explorar Contos" description="Descobre os melhores contos eróticos por categoria." type="website" />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-20 md:pb-12 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 md:mb-8">
            <h1 className="font-brand text-2xl md:text-4xl font-bold text-foreground mb-1 md:mb-2">
              {currentCategory ? currentCategory.name : 'Explorar Contos'}
            </h1>
            {currentCategory && (
              <p className="text-sm md:text-base text-muted-foreground">{currentCategory.description}</p>
            )}
          </div>

          {/* 1. Search + sort/category */}
          <div className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar contos..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                data-testid="browse-search"
                className="pl-9 md:pl-10 bg-surface border-border h-10 md:h-12 text-sm md:text-base"
              />
              {filters.search && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="flex gap-2 md:gap-4">
              <Select
                value={filters.category}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, category: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="flex-1 md:w-48 bg-surface border-border h-10 md:h-12 text-sm" data-testid="browse-category-select">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.sortBy}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, sortBy: value }))}
              >
                <SelectTrigger className="flex-1 md:w-48 bg-surface border-border h-10 md:h-12 text-sm" data-testid="browse-sort-select">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="recent">Recentes</SelectItem>
                  <SelectItem value="popular">Populares</SelectItem>
                  <SelectItem value="rating">Avaliados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 2. Category Pills */}
          <div className="flex gap-2 mb-8 md:mb-10 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
            <Button
              variant={!filters.category ? "default" : "outline"}
              onClick={() => setFilters((prev) => ({ ...prev, category: '' }))}
              className={`rounded-full text-xs md:text-sm px-3 md:px-4 h-8 md:h-9 flex-shrink-0 ${!filters.category ? 'bg-ruby hover:bg-ruby-light' : 'border-border'}`}
            >
              Todos
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.slug}
                variant={filters.category === cat.slug ? "default" : "outline"}
                onClick={() => setFilters((prev) => ({ ...prev, category: cat.slug }))}
                data-testid={`filter-${cat.slug}`}
                className={`rounded-full text-xs md:text-sm px-3 md:px-4 h-8 md:h-9 flex-shrink-0 ${filters.category === cat.slug ? 'bg-ruby hover:bg-ruby-light' : 'border-border'}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-ruby"></div>
            </div>
          ) : (
            <>
              {/* 3. ✨ Contos Intimate — preview */}
              <section className="mb-10 md:mb-14" data-testid="section-intimate-originals">
                <div className="mb-5">
                  <h2 className="font-brand text-xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-ruby" />
                    Contos Intimate
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 max-w-3xl">
                    Os contos originais da Intimate, escritos pela equipa editorial e publicados exclusivamente na plataforma.
                  </p>
                </div>
                {intimateStories.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                    {intimateStories.slice(0, PREVIEW_LIMIT).map((story) => (
                      <StoryCard key={story.id} story={story} showOfficialBadge />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-6">Ainda não há contos originais nesta categoria.</p>
                )}

                {/* 4. Ver todos os Contos Intimate */}
                <div className="mt-6 md:mt-8 flex justify-center">
                  <Button
                    onClick={() => navigate(`/stories/intimate${querySuffix}`)}
                    data-testid="view-all-intimate"
                    className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6 md:px-8 py-5 md:py-6 font-semibold text-sm md:text-base shadow-lg active:scale-95"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Ver todos os Contos Intimate
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </section>

              {/* 5. Explorar por autor */}
              {communityAuthors.length > 0 && (
                <section className="mb-8 md:mb-10" data-testid="section-community-authors-filter">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-brand text-lg md:text-2xl font-semibold text-foreground flex items-center gap-2">
                      <Users className="w-5 h-5 md:w-6 md:h-6 text-ruby" />
                      Explorar por autor
                    </h3>
                    {filters.authorId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilters((prev) => ({ ...prev, authorId: '' }))}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="clear-author-filter"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Limpar filtro
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
                    {communityAuthors.map((a) => {
                      const active = filters.authorId === a.id;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => setFilters((prev) => ({
                            ...prev,
                            authorId: prev.authorId === a.id ? '' : a.id,
                          }))}
                          data-testid={`author-filter-${a.id}`}
                          className={
                            'group flex flex-col items-center gap-2 shrink-0 w-[100px] md:w-[120px] p-3 rounded-2xl border transition-all ' +
                            (active
                              ? 'border-ruby bg-ruby/10'
                              : 'border-border bg-surface hover:border-ruby/50')
                          }
                        >
                          <AuthorAvatar name={a.name} src={a.avatar_url} />
                          <span className="text-xs md:text-sm font-medium text-foreground line-clamp-2 text-center">
                            {a.name}
                          </span>
                          <span className="text-[10px] md:text-xs text-muted-foreground">
                            {a.story_count} {a.story_count === 1 ? 'conto' : 'contos'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* 6. ✍️ Contos da Comunidade — preview */}
              <section data-testid="section-community-stories">
                <div className="mb-5">
                  <h2 className="font-brand text-xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                    <PenLine className="w-5 h-5 md:w-6 md:h-6 text-ruby" />
                    Contos da Comunidade
                    {selectedAuthor && (
                      <span className="text-base md:text-lg font-normal text-muted-foreground ml-2">
                        · por {selectedAuthor.name}
                      </span>
                    )}
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 max-w-3xl">
                    Descobre histórias escritas pelos autores da comunidade Intimate. Novas vozes, novos estilos e novas fantasias.
                  </p>
                </div>
                {communityFiltered.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                    {communityFiltered.slice(0, PREVIEW_LIMIT).map((story) => (
                      <StoryCard key={story.id} story={story} showAuthorLink />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-6">
                    {filters.authorId
                      ? 'Este autor ainda não tem contos publicados nesta categoria.'
                      : 'Ainda não há contos da comunidade nesta categoria. Se és autor(a), submete o teu!'}
                  </p>
                )}

                {/* 7. Ver todos os Contos da Comunidade */}
                <div className="mt-6 md:mt-8 flex justify-center">
                  <Button
                    onClick={() => navigate(`/stories/community${querySuffix}${filters.authorId ? (querySuffix ? '&' : '?') + 'author=' + filters.authorId : ''}`)}
                    data-testid="view-all-community"
                    className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6 md:px-8 py-5 md:py-6 font-semibold text-sm md:text-base shadow-lg active:scale-95"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Ver todos os Contos da Comunidade
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* 8. Call-to-action: escreve */}
                <div
                  className="mt-10 md:mt-14 rounded-2xl overflow-hidden border border-ruby/30 bg-gradient-to-br from-ruby/15 via-background to-black relative"
                  data-testid="community-invite-write"
                >
                  <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-ruby/25 blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
                  <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8 p-6 md:p-10">
                    <div className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-ruby/20 border border-ruby/40 flex items-center justify-center">
                      <PenLine className="w-7 h-7 md:w-8 md:h-8 text-ruby" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Badge className="bg-ruby/20 text-ruby border-ruby/40 hover:bg-ruby/30 mb-2 w-fit">
                        Autores da Comunidade
                      </Badge>
                      <h3 className="font-brand text-xl md:text-2xl font-bold text-foreground mb-2 leading-tight">
                        Também tu podes escrever na Intimate
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
                        Tens uma história que arde por ser contada? Partilha-a com a comunidade — publicamos os contos aprovados na secção <strong className="text-foreground">Autores</strong> e os leitores agradecem.
                      </p>
                    </div>
                    <div className="shrink-0">
                      <Button
                        onClick={() => navigate('/write')}
                        data-testid="community-invite-write-cta"
                        className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6 md:px-8 py-5 md:py-6 font-semibold text-sm md:text-base w-full md:w-auto shadow-lg active:scale-95"
                      >
                        <PenLine className="w-4 h-4 mr-2" />
                        Escrever o meu conto
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
